import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toPng } from 'html-to-image';
import { Play, StopCircle, Download, RefreshCw, X, Minus, History, Trophy, Trash2, IdCard, Pencil, Save } from 'lucide-react';
import { API } from './adminUtils';

const SLOT_LABELS = {
  GR: 'Guarda-Redes',
  DE: 'Defesa Esq',
  DC: 'Defesa Cen',
  DD: 'Defesa Dir',
  AE: 'Atacante Esq',
  AC: 'Atacante Cen',
  AD: 'Atacante Dir',
  MC: 'Meio-Campo',
};

// Short button abbreviations per stat key (fallback = first 3 chars uppercase)
const STAT_LABEL_MAP = {
  goals: 'GOL', assists: 'AST',
  shots_on_target: 'RB', shots_missed: 'RF',
  passes_completed: 'PC', passes_failed: 'PE',
  tackles: 'DES', blocks: 'BLO',
  fouls_committed: 'FLT', own_goals: 'GC',
  saves: 'DEF', goals_conceded: 'GS',
  clean_sheets: 'JSG', dribbles: 'FIN',
  interceptions: 'INT', throws: 'LAN',
  possession_lost: 'PPB', control_errors: 'ERR',
};

// Stats calculated automatically by the backend — no manual +/- buttons
const AUTO_STATS = new Set([
  'wins', 'draws', 'losses', 'games_played',
  'clean_sheets', 'goals_conceded',
]);

// Positive stats = green shades, negative = red/orange shades
const posBg = (weight, enabled) => {
  if (!enabled) return 'bg-zinc-400';
  return weight >= 0 ? 'bg-green-600 hover:bg-green-500' : 'bg-red-600 hover:bg-red-500';
};

// Slot positions on horizontal half-pitch (percentages of container)
const SLOT_POSITIONS = {
  GR: { top: '80%', left: '50%' },
  DE: { top: '58%', left: '25%' },
  DD: { top: '58%', left: '75%' },
  AE: { top: '30%', left: '30%' },
  AD: { top: '30%', left: '70%' },
};

const LiveGameTab = ({ ctx }) => {
  const { players: allPlayers } = ctx;
  const [live, setLive] = useState(null);
  const [ratingCfg, setRatingCfg] = useState([]);
  const [statCounters, setStatCounters] = useState({});
  const [loading, setLoading] = useState(true);
  const [exportSide, setExportSide] = useState(null);
  const [showCards, setShowCards] = useState(false);
  const [endSummary, setEndSummary] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const exportRef = useRef(null);

  // 7 slots cover any team from 5 up to 8 players. GR is reserved for position 0.
  const SLOT_OPTIONS = ['GR', 'DE', 'DC', 'DD', 'AE', 'AC', 'AD', 'MC'];
  const DEFAULT_SLOTS = ['GR', 'DE', 'DD', 'AE', 'AD', 'DC', 'AC', 'MC']; // first N used

  const makeTeam = (size) => Array.from({ length: size }, (_, i) => ({
    name: '', slot: DEFAULT_SLOTS[i] || 'MC',
  }));
  const [teamSize, setTeamSize] = useState(5);
  const [form, setForm] = useState({
    name: '', date: new Date().toISOString().slice(0, 10),
    team_a_name: 'Time A', team_b_name: 'Time B',
    team_a: makeTeam(5), team_b: makeTeam(5),
  });

  const changeTeamSize = (newSize) => {
    setTeamSize(newSize);
    setForm((f) => ({
      ...f,
      team_a: Array.from({ length: newSize }, (_, i) => f.team_a[i] || { name: '', slot: DEFAULT_SLOTS[i] || 'MC' }),
      team_b: Array.from({ length: newSize }, (_, i) => f.team_b[i] || { name: '', slot: DEFAULT_SLOTS[i] || 'MC' }),
    }));
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const [liveRes, cfgRes] = await Promise.all([
        axios.get(`${API}/admin/live/current`),
        axios.get(`${API}/rating-config`),
      ]);
      setLive(liveRes.data?.live || null);
      setRatingCfg(cfgRes.data?.config || []);
      if (liveRes.data?.live?.active) {
        const s = await axios.get(`${API}/admin/live/player-stats`);
        setStatCounters(s.data?.stats || {});
      }
    } catch { /* noop */ }
    setLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  const start = async () => {
    const allNames = [...form.team_a, ...form.team_b].map(p => p.name).filter(Boolean);
    const expected = teamSize * 2;
    if (allNames.length !== expected) { alert(`Escolhe ${expected} jogadores (${teamSize} por time)`); return; }
    if (new Set(allNames).size !== expected) { alert('Um jogador não pode estar nos 2 times'); return; }
    try {
      await axios.post(`${API}/admin/live/start`, form);
      const s = await axios.get(`${API}/admin/live/player-stats`);
      setStatCounters(s.data?.stats || {});
      refresh();
    } catch (e) { alert(e.response?.data?.detail || 'Erro ao iniciar'); }
  };

  const changeStat = async (playerName, stat, team, delta) => {
    // Optimistic UI update on counters
    setStatCounters(prev => {
      const cur = prev[playerName] || {};
      const nextVal = Math.max(0, (cur[stat] || 0) + delta);
      return { ...prev, [playerName]: { ...cur, [stat]: nextVal } };
    });
    try {
      const r = await axios.post(`${API}/admin/live/increment`, { player_name: playerName, stat, team, delta });
      setLive(r.data?.live || null);
      // Refresh counters from server truth (includes goals_conceded cascade)
      const s = await axios.get(`${API}/admin/live/player-stats`);
      setStatCounters(s.data?.stats || {});
    } catch (e) {
      alert(e.response?.data?.detail || 'Erro');
      const s = await axios.get(`${API}/admin/live/player-stats`);
      setStatCounters(s.data?.stats || {});
    }
  };

  const endGame = async () => {
    if (!window.confirm('Terminar o jogo? Vai contabilizar V/E/D e Jogos de todos os 10 jogadores.')) return;
    try {
      const r = await axios.post(`${API}/admin/live/end`);
      setEndSummary(r.data?.summary || null);
      refresh();
    } catch { alert('Erro ao terminar'); }
  };

  const doExport = async () => {
    if (!exportRef.current) return;
    try {
      const dataUrl = await toPng(exportRef.current, {
        pixelRatio: 2, cacheBust: true, width: 1920, height: 1080,
        backgroundColor: '#0d5f2a',
      });
      const link = document.createElement('a');
      const side = exportSide === 'a' ? live.team_a_name : live.team_b_name;
      link.download = `escalacao_${side.replace(/\s+/g, '_')}.png`;
      link.href = dataUrl;
      link.click();
    } catch (e) { alert('Erro ao exportar: ' + e.message); }
  };

  // Active stats derived from rating-config (only enabled + non-auto stats show as buttons)
  const activeStats = ratingCfg.filter(c => c.enabled !== false && !AUTO_STATS.has(c.key));

  const renderSetup = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-bold uppercase mb-1">Data</label>
          <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="input-field" data-testid="live-date" />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase mb-1">Nome / Etiqueta</label>
          <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Domingão 20/07" className="input-field" data-testid="live-name" />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase mb-1">Jogadores / Time</label>
          <select value={teamSize} onChange={(e) => changeTeamSize(parseInt(e.target.value, 10))} className="input-field font-bold" data-testid="live-team-size">
            {[5, 6, 7, 8].map((n) => <option key={n} value={n}>{n} vs {n}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {['a', 'b'].map(teamKey => (
          <div key={teamKey} className={`border-2 rounded-lg p-4 ${teamKey === 'a' ? 'border-red-500 bg-red-50/30' : 'border-blue-500 bg-blue-50/30'}`}>
            <label className="block text-xs font-bold uppercase mb-1">Nome do {teamKey === 'a' ? 'Time A' : 'Time B'}</label>
            <input
              type="text"
              value={form[`team_${teamKey}_name`]}
              onChange={(e) => setForm({ ...form, [`team_${teamKey}_name`]: e.target.value })}
              className="input-field mb-3"
              data-testid={`live-team-${teamKey}-name`}
            />
            {form[`team_${teamKey}`].map((row, idx) => (
              <div key={idx} className="flex gap-2 mb-2">
                <select
                  value={row.slot}
                  onChange={(e) => {
                    const next = [...form[`team_${teamKey}`]];
                    next[idx] = { ...next[idx], slot: e.target.value };
                    setForm({ ...form, [`team_${teamKey}`]: next });
                  }}
                  className="border-2 border-black px-2 py-1 font-bold text-sm bg-white"
                  data-testid={`live-slot-${teamKey}-${idx}`}
                >
                  {Object.entries(SLOT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <select
                  value={row.name}
                  onChange={(e) => {
                    const next = [...form[`team_${teamKey}`]];
                    next[idx] = { ...next[idx], name: e.target.value };
                    setForm({ ...form, [`team_${teamKey}`]: next });
                  }}
                  className="flex-1 border-2 border-black px-2 py-1 font-medium text-sm bg-white"
                  data-testid={`live-player-${teamKey}-${idx}`}
                >
                  <option value="">— Escolher jogador —</option>
                  {allPlayers.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
                </select>
              </div>
            ))}
          </div>
        ))}
      </div>

      <button onClick={start} className="btn-primary" data-testid="live-start">
        <Play className="inline mr-2" size={20} /> INICIAR JOGO
      </button>
    </div>
  );

  const renderLive = () => (
    <>
      <div className="border-2 border-black rounded-lg overflow-hidden">
        <div className="bg-zinc-900 text-white p-4 flex items-center justify-between flex-wrap gap-3">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-widest opacity-70">{live.date} · {live.name || 'Jogo ao vivo'}</p>
          </div>
          <div className="flex items-center gap-6 text-3xl sm:text-4xl font-black tabular-nums">
            <span className="text-red-400 truncate max-w-[140px]" title={live.team_a_name}>{live.team_a_name}</span>
            <span className="text-yellow-300" data-testid="live-score">
              {live.score_a} <span className="opacity-40">-</span> {live.score_b}
            </span>
            <span className="text-blue-300 truncate max-w-[140px]" title={live.team_b_name}>{live.team_b_name}</span>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setShowCards(true)} className="bg-purple-600 hover:bg-purple-500 text-white font-black px-3 py-2 rounded flex items-center gap-1 text-sm" data-testid="show-player-cards">
              <IdCard size={16} /> CARTAS
            </button>
            <button onClick={() => setExportSide('a')} className="bg-yellow-500 hover:bg-yellow-400 text-black font-black px-3 py-2 rounded flex items-center gap-1 text-sm" data-testid="export-team-a">
              <Download size={16} /> {live.team_a_name}
            </button>
            <button onClick={() => setExportSide('b')} className="bg-yellow-500 hover:bg-yellow-400 text-black font-black px-3 py-2 rounded flex items-center gap-1 text-sm" data-testid="export-team-b">
              <Download size={16} /> {live.team_b_name}
            </button>
            <button onClick={endGame} className="bg-red-600 hover:bg-red-500 text-white font-black px-3 py-2 rounded flex items-center gap-1 text-sm" data-testid="live-end">
              <StopCircle size={16} /> TERMINAR
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TeamPanel side="a" teamName={live.team_a_name} players={live.team_a} activeStats={activeStats} counters={statCounters} onChange={changeStat} accent="red" />
        <TeamPanel side="b" teamName={live.team_b_name} players={live.team_b} activeStats={activeStats} counters={statCounters} onChange={changeStat} accent="blue" />
      </div>

      {exportSide && (
        <ExportOverlay
          live={live}
          side={exportSide}
          allPlayers={allPlayers}
          exportRef={exportRef}
          onClose={() => setExportSide(null)}
          onDownload={doExport}
        />
      )}

      {showCards && (
        <PlayerCardsOverlay
          live={live}
          onClose={() => setShowCards(false)}
        />
      )}
    </>
  );

  return (
    <div className="card space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-3xl font-bold mb-1 flex items-center gap-2">🎮 JOGO AO VIVO</h2>
          <p className="text-sm text-zinc-500">Controlo em tempo real do jogo 5v5. Os contadores <span className="font-semibold">zeram sempre ao iniciar uma nova partida</span>. <span className="font-semibold">V/E/D, Jogos, JSG e GS são calculados automaticamente</span> no final.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowHistory(true)} className="btn-secondary text-sm py-2 px-3 flex items-center gap-1" title="Histórico" data-testid="live-history-btn">
            <History size={16} /> HISTÓRICO
          </button>
          <button onClick={refresh} className="btn-secondary text-sm py-2 px-3" title="Atualizar"><RefreshCw size={16} /></button>
        </div>
      </div>

      {loading ? <p className="text-zinc-400 text-center py-6">A carregar…</p>
        : (live && live.active) ? renderLive() : renderSetup()}

      {endSummary && <EndSummaryModal summary={endSummary} onClose={() => setEndSummary(null)} />}
      {showHistory && <HistoryModal onClose={() => setShowHistory(false)} allPlayers={allPlayers} />}
    </div>
  );
};

const TeamPanel = ({ side, teamName, players, activeStats, counters, onChange, accent }) => (
  <div className={`border-2 ${accent === 'red' ? 'border-red-500' : 'border-blue-500'} rounded-lg overflow-hidden`}>
    <div className={`p-2 font-black uppercase text-white text-center ${accent === 'red' ? 'bg-red-600' : 'bg-blue-600'}`}>{teamName}</div>
    <div className="p-2 space-y-2">
      {(players || []).map((pl) => {
        const cur = counters[pl.name] || {};
        return (
          <div key={pl.name} className="border-2 border-black rounded p-2 bg-white">
            <div className="flex items-center justify-between mb-2">
              <p className="font-black uppercase truncate">{pl.name}</p>
              <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{SLOT_LABELS[pl.slot] || pl.slot}</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-1.5">
              {activeStats.map((cfg) => {
                const label = STAT_LABEL_MAP[cfg.key] || cfg.label.slice(0, 3).toUpperCase();
                const value = cur[cfg.key] || 0;
                return (
                  <div key={cfg.key} className="flex items-stretch rounded overflow-hidden shadow-sm">
                    <button
                      onClick={() => onChange(pl.name, cfg.key, side, +1)}
                      className={`${posBg(cfg.weight, cfg.enabled)} text-white text-[11px] font-black py-1.5 px-2 flex-1 active:scale-95 transition-transform`}
                      data-testid={`inc-${side}-${pl.name}-${cfg.key}`}
                      title={cfg.label}
                    >
                      +{label}
                      <span className="ml-1 bg-black/25 rounded px-1 tabular-nums">{value}</span>
                    </button>
                    <button
                      onClick={() => onChange(pl.name, cfg.key, side, -1)}
                      className="bg-black/70 hover:bg-black/90 text-white px-1 flex items-center justify-center"
                      data-testid={`dec-${side}-${pl.name}-${cfg.key}`}
                      title={`Remover 1 ${cfg.label}`}
                    >
                      <Minus size={12} strokeWidth={3} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

// ==================== END-OF-GAME SUMMARY MODAL ====================
const EndSummaryModal = ({ summary, onClose }) => {
  const [ratingCfg, setRatingCfg] = useState([]);
  useEffect(() => {
    axios.get(`${API}/rating-config`).then(r => setRatingCfg(r.data?.config || []))
      .catch(() => {});
  }, []);

  const winnerName = summary.winner === 'a' ? summary.team_a_name
    : summary.winner === 'b' ? summary.team_b_name : null;

  // Aggregate match_stats totals per team
  const aggregateTeam = (teamPlayers) => {
    const tot = {};
    for (const pl of (teamPlayers || [])) {
      const s = summary.match_stats?.[pl.name] || {};
      for (const [k, v] of Object.entries(s)) {
        tot[k] = (tot[k] || 0) + Number(v || 0);
      }
    }
    return tot;
  };
  const totA = aggregateTeam(summary.team_a);
  const totB = aggregateTeam(summary.team_b);

  // Rows to show: enabled + non-auto stats where at least one side has > 0
  const AUTO = new Set(['wins', 'draws', 'losses', 'games_played', 'clean_sheets', 'goals_conceded']);
  const rows = (ratingCfg || [])
    .filter(c => c.enabled !== false && !AUTO.has(c.key))
    .map(c => ({ key: c.key, label: c.label, a: totA[c.key] || 0, b: totB[c.key] || 0 }))
    .filter(r => r.a > 0 || r.b > 0);

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full my-4 border-4 border-yellow-500 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="end-summary-modal">
        <div className="bg-gradient-to-r from-yellow-500 to-yellow-400 p-4 flex items-center justify-between rounded-t-xl">
          <div className="flex items-center gap-2">
            <Trophy size={28} className="text-black" />
            <h3 className="text-2xl font-black uppercase text-black">Fim de Jogo</h3>
          </div>
          <button onClick={onClose} className="bg-black/20 hover:bg-black/40 text-black p-1.5 rounded" data-testid="close-summary">
            <X size={20} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="text-center">
            <p className="text-xs uppercase tracking-widest text-zinc-500">{summary.date} · {summary.name || 'Jogo ao vivo'}</p>
            <div className="flex items-center justify-center gap-4 text-4xl font-black tabular-nums mt-2">
              <span className={summary.winner === 'a' ? 'text-green-600' : 'text-zinc-500'}>{summary.team_a_name}</span>
              <span className="text-black">{summary.score_a} <span className="opacity-40">-</span> {summary.score_b}</span>
              <span className={summary.winner === 'b' ? 'text-green-600' : 'text-zinc-500'}>{summary.team_b_name}</span>
            </div>
            {winnerName ? (
              <p className="mt-3 text-lg font-black text-green-700 uppercase">🏆 {winnerName} venceu!</p>
            ) : (
              <p className="mt-3 text-lg font-black text-orange-600 uppercase">🤝 Empate!</p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <HighlightCard title="Artilheiro" tone="red" leader={summary.top_scorer} suffix="golo(s)" />
            <HighlightCard title="Garçom" tone="blue" leader={summary.top_assist} suffix="assist." />
            <HighlightCard title="Muralha" tone="green" leader={summary.top_saves} suffix="defesa(s)" />
          </div>

          {/* World Cup-style team stats table */}
          <div className="bg-zinc-900 rounded-xl overflow-hidden" data-testid="team-stats-table">
            <p className="text-xs uppercase tracking-widest text-zinc-300 font-black px-4 pt-3">📊 Estatísticas do Jogo</p>
            <div className="grid grid-cols-[1fr_2fr_1fr] items-center px-4 py-2 border-b border-zinc-700">
              <span className="text-red-400 font-black uppercase text-sm truncate text-left" title={summary.team_a_name}>{summary.team_a_name}</span>
              <span className="text-zinc-500 text-[10px] text-center uppercase tracking-widest">Principais estatísticas</span>
              <span className="text-blue-400 font-black uppercase text-sm truncate text-right" title={summary.team_b_name}>{summary.team_b_name}</span>
            </div>
            {rows.length === 0 ? (
              <p className="text-zinc-500 text-center italic py-6 text-sm">— sem registos —</p>
            ) : (
              <div className="divide-y divide-zinc-800">
                {rows.map(r => <StatRow key={r.key} row={r} />)}
              </div>
            )}
          </div>

          <details className="border-2 border-zinc-300 rounded-lg">
            <summary className="cursor-pointer px-3 py-2 font-black uppercase text-xs text-zinc-700 bg-zinc-100">
              Detalhes por Jogador
            </summary>
            <div className="p-2 max-h-64 overflow-y-auto text-xs">
              {Object.entries(summary.per_player_summary || {}).map(([name, s]) => (
                <div key={name} className="flex justify-between border-b border-zinc-200 py-1">
                  <span className="font-bold">{name} <span className="text-zinc-400 font-normal">({s.slot})</span></span>
                  <span className="text-zinc-600 tabular-nums">
                    {Object.entries(s).filter(([k]) => !['team', 'slot'].includes(k))
                      .map(([k, v]) => `${STAT_LABEL_MAP[k] || k.toUpperCase()}:${v}`).join(' · ')}
                  </span>
                </div>
              ))}
            </div>
          </details>

          <p className="text-[11px] text-zinc-500 text-center">Todas as estatísticas foram somadas ao mês corrente de cada jogador. Consulta o histórico completo no botão &quot;HISTÓRICO&quot;.</p>
        </div>
      </div>
    </div>
  );
};

// Stat row (World Cup layout) — winner side highlighted with red pill
const StatRow = ({ row }) => {
  const aWins = row.a > row.b;
  const bWins = row.b > row.a;
  return (
    <div className="grid grid-cols-[1fr_2fr_1fr] items-center px-4 py-2.5" data-testid={`stat-row-${row.key}`}>
      <div className="text-left">
        <span className={`inline-block min-w-[36px] text-center tabular-nums font-black text-lg ${aWins ? 'bg-red-600 text-white px-2.5 py-0.5 rounded-full' : 'text-zinc-200'}`}>
          {row.a}
        </span>
      </div>
      <div className="text-center text-zinc-300 text-sm font-medium truncate px-2">{row.label}</div>
      <div className="text-right">
        <span className={`inline-block min-w-[36px] text-center tabular-nums font-black text-lg ${bWins ? 'bg-red-600 text-white px-2.5 py-0.5 rounded-full' : 'text-zinc-200'}`}>
          {row.b}
        </span>
      </div>
    </div>
  );
};

const HighlightCard = ({ title, tone, leader, suffix }) => {
  const toneMap = {
    red: 'border-red-500 bg-red-50 text-red-700',
    blue: 'border-blue-500 bg-blue-50 text-blue-700',
    green: 'border-green-500 bg-green-50 text-green-700',
  };
  return (
    <div className={`border-2 rounded-lg p-3 ${toneMap[tone]}`}>
      <p className="text-[10px] font-black uppercase tracking-widest opacity-80">{title}</p>
      {leader ? (
        <>
          <p className="text-base font-black truncate">{leader.name}</p>
          <p className="text-xs opacity-80">{leader.value} {suffix}</p>
        </>
      ) : (
        <p className="text-xs opacity-60 italic">— sem registos —</p>
      )}
    </div>
  );
};

// ==================== HISTORY MODAL ====================
const HistoryModal = ({ onClose, allPlayers }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/live/history`);
      setHistory(r.data?.history || []);
    } catch { /* noop */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm('Apagar esta partida do histórico? (não afeta os stats já aplicados aos jogadores)')) return;
    try {
      await axios.delete(`${API}/admin/live/history/${id}`);
      load();
    } catch { alert('Erro ao apagar'); }
  };

  const [reconciling, setReconciling] = useState(false);
  const reconcilePastMonth = async () => {
    if (!window.confirm('Vai mover para o All-Time os stats de todas as partidas com data de mês passado que foram arquivadas antes do fix. Zera o impacto no "Mês Atual". Confirmar?')) return;
    setReconciling(true);
    try {
      const r = await axios.post(`${API}/admin/stats/reconcile-past-month?password=domingao2025`);
      const d = r.data;
      alert(`✅ Correção aplicada:\n\nPartidas corrigidas: ${d.reconciled_matches}\nJogadores afetados: ${d.players_affected}\nStats movidas: ${Object.values(d.changes).reduce((a, b) => a + b.monthly_removed, 0)}`);
      load();
    } catch (ex) {
      alert('Erro ao reconciliar: ' + (ex?.response?.data?.detail || ex.message));
    }
    setReconciling(false);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col border-4 border-black" onClick={(e) => e.stopPropagation()} data-testid="history-modal">
        <div className="bg-black text-white p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <History size={22} />
            <h3 className="text-xl font-black uppercase">Histórico de Partidas Live</h3>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={reconcilePastMonth} disabled={reconciling}
              className="bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black text-xs font-black uppercase rounded px-3 py-1.5"
              title="Mover para All-Time os stats de partidas de meses passados que foram arquivadas antes do fix"
              data-testid="reconcile-past-month-btn">
              {reconciling ? 'A corrigir…' : 'Corrigir Mês Passado'}
            </button>
            <button onClick={onClose} className="bg-white/10 hover:bg-white/20 text-white p-1.5 rounded" data-testid="close-history">
              <X size={20} />
            </button>
          </div>
        </div>
        <div className="p-4 overflow-y-auto flex-1">
          {loading ? (
            <p className="text-zinc-500 text-center py-6">A carregar…</p>
          ) : history.length === 0 ? (
            <p className="text-zinc-500 text-center py-6 italic">Ainda não existem partidas terminadas.</p>
          ) : selected ? (
            <MatchDetail match={selected} onBack={() => setSelected(null)} onSaved={(m) => { setSelected(m); load(); }} allPlayers={allPlayers} />
          ) : (
            <div className="space-y-2">
              {history.map((m) => {
                const winnerName = m.winner === 'a' ? m.team_a_name
                  : m.winner === 'b' ? m.team_b_name : null;
                return (
                  <div key={m.id} className="border-2 border-zinc-300 hover:border-black rounded-lg p-3 flex items-center justify-between gap-3 transition-colors" data-testid={`history-item-${m.id}`}>
                    <button onClick={() => setSelected(m)} className="flex-1 text-left">
                      <p className="text-xs text-zinc-500 uppercase tracking-widest">{m.date} · {m.name || '—'}</p>
                      <p className="text-lg font-black tabular-nums">
                        <span className={m.winner === 'a' ? 'text-green-700' : ''}>{m.team_a_name}</span>
                        <span className="mx-2 text-zinc-400">{m.score_a} - {m.score_b}</span>
                        <span className={m.winner === 'b' ? 'text-green-700' : ''}>{m.team_b_name}</span>
                      </p>
                      {winnerName && <p className="text-xs font-bold text-green-700">🏆 {winnerName}</p>}
                      {!winnerName && <p className="text-xs font-bold text-orange-600">🤝 Empate</p>}
                    </button>
                    <button onClick={() => remove(m.id)} className="bg-red-600 hover:bg-red-500 text-white p-2 rounded" title="Apagar" data-testid={`del-history-${m.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const MatchDetail = ({ match, onBack, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    date: match.date || '', name: match.name || '',
    team_a_name: match.team_a_name || '', team_b_name: match.team_b_name || '',
    score_a: match.score_a || 0, score_b: match.score_b || 0,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/live/history/${match.id}`, {
        ...form,
        score_a: Number(form.score_a),
        score_b: Number(form.score_b),
      });
      setEditing(false);
      if (onSaved && r.data?.match) onSaved({ ...match, ...r.data.match });
    } catch (e) {
      alert(e.response?.data?.detail || 'Erro ao guardar');
    }
    setSaving(false);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <button onClick={onBack} className="btn-secondary text-sm py-1 px-3">← Voltar à lista</button>
        {!editing ? (
          <button onClick={() => setEditing(true)} className="bg-yellow-500 hover:bg-yellow-400 text-black font-black px-3 py-1.5 rounded flex items-center gap-1 text-sm" data-testid="edit-match-btn">
            <Pencil size={14} /> EDITAR
          </button>
        ) : (
          <div className="flex gap-1">
            <button onClick={save} disabled={saving} className="bg-green-600 hover:bg-green-500 text-white font-black px-3 py-1.5 rounded flex items-center gap-1 text-sm" data-testid="save-match-btn">
              <Save size={14} /> {saving ? '...' : 'GUARDAR'}
            </button>
            <button onClick={() => setEditing(false)} className="bg-zinc-300 hover:bg-zinc-400 px-3 py-1.5 rounded text-sm">
              <X size={14} />
            </button>
          </div>
        )}
      </div>

      {editing ? (
        <div className="border-2 border-yellow-500 bg-yellow-50 rounded-lg p-3 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs font-bold">
              Data
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="input-field mt-1" data-testid="edit-date" />
            </label>
            <label className="text-xs font-bold">
              Nome / Etiqueta
              <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-field mt-1" data-testid="edit-name" />
            </label>
            <label className="text-xs font-bold">
              Time A
              <input type="text" value={form.team_a_name} onChange={(e) => setForm({ ...form, team_a_name: e.target.value })} className="input-field mt-1" data-testid="edit-team-a" />
            </label>
            <label className="text-xs font-bold">
              Time B
              <input type="text" value={form.team_b_name} onChange={(e) => setForm({ ...form, team_b_name: e.target.value })} className="input-field mt-1" data-testid="edit-team-b" />
            </label>
            <label className="text-xs font-bold">
              Placar A
              <input type="number" min="0" value={form.score_a} onChange={(e) => setForm({ ...form, score_a: e.target.value })} className="input-field mt-1" data-testid="edit-score-a" />
            </label>
            <label className="text-xs font-bold">
              Placar B
              <input type="number" min="0" value={form.score_b} onChange={(e) => setForm({ ...form, score_b: e.target.value })} className="input-field mt-1" data-testid="edit-score-b" />
            </label>
          </div>
          <p className="text-[11px] text-zinc-600">💡 Nota: alterar o placar recalcula o vencedor automaticamente, mas <strong>não</strong> altera stats já aplicadas aos jogadores.</p>
        </div>
      ) : (
        <div className="text-center border-2 border-black rounded-lg p-4 bg-zinc-50">
          <p className="text-xs uppercase tracking-widest text-zinc-500">{match.date} · {match.name || 'Jogo'}</p>
          <p className="text-3xl font-black tabular-nums mt-1">
            <span className={match.winner === 'a' ? 'text-green-700' : ''}>{match.team_a_name}</span>
            <span className="mx-3 text-zinc-400">{match.score_a} - {match.score_b}</span>
            <span className={match.winner === 'b' ? 'text-green-700' : ''}>{match.team_b_name}</span>
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <HighlightCard title="Artilheiro" tone="red" leader={match.top_scorer} suffix="golo(s)" />
        <HighlightCard title="Garçom" tone="blue" leader={match.top_assist} suffix="assist." />
        <HighlightCard title="Muralha" tone="green" leader={match.top_saves} suffix="defesa(s)" />
      </div>
      {match.clean_sheet_names?.length > 0 && (
        <div className="border-2 border-green-500 bg-green-50 rounded-lg p-2">
          <p className="text-xs font-black uppercase text-green-700 mb-1">🧤 JSG</p>
          <p className="text-sm text-green-900">{match.clean_sheet_names.join(', ')}</p>
        </div>
      )}
      <div className="border-2 border-zinc-300 rounded-lg p-2 max-h-72 overflow-y-auto text-xs">
        <p className="text-xs font-black uppercase mb-1">Detalhes por Jogador</p>
        {Object.entries(match.per_player_summary || {}).map(([name, s]) => (
          <div key={name} className="flex justify-between border-b border-zinc-200 py-1">
            <span className="font-bold">{name} <span className="text-zinc-400 font-normal">({s.slot})</span></span>
            <span className="text-zinc-600 tabular-nums">
              {Object.entries(s).filter(([k]) => !['team', 'slot'].includes(k))
                .map(([k, v]) => `${STAT_LABEL_MAP[k] || k.toUpperCase()}:${v}`).join(' · ')}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ==================== EXPORT OVERLAY ====================
const ExportOverlay = ({ live, side, allPlayers, exportRef, onClose, onDownload }) => {
  const players = side === 'a' ? live.team_a : live.team_b;
  const teamName = side === 'a' ? live.team_a_name : live.team_b_name;
  const playerMap = Object.fromEntries(allPlayers.map(p => [p.name, p]));

  return (
    <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4" onClick={onClose}>
      <div className="max-w-6xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center text-white mb-3">
          <p className="font-black uppercase text-lg">Escalação · {teamName}</p>
          <div className="flex gap-2">
            <button onClick={onDownload} className="bg-yellow-500 hover:bg-yellow-400 text-black font-black px-4 py-2 rounded flex items-center gap-2" data-testid="download-png">
              <Download size={16} /> DOWNLOAD PNG
            </button>
            <button onClick={onClose} className="bg-white/10 hover:bg-white/20 text-white p-2 rounded">
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="w-full overflow-auto rounded-xl bg-black/50 p-2">
          <div style={{ width: 1920, height: 1080, transformOrigin: 'top left' }}
            className="scale-[0.48] sm:scale-[0.55] md:scale-[0.6] origin-top-left">
            <PitchCanvas ref={exportRef} teamName={teamName} players={players} playerMap={playerMap} />
          </div>
          <div style={{ height: 0 }} />
        </div>
      </div>
    </div>
  );
};

const PitchCanvas = React.forwardRef(({ teamName, players, playerMap }, ref) => (
  <div
    ref={ref}
    style={{
      width: 1920, height: 1080,
      background: 'linear-gradient(180deg, #0d5f2a 0%, #128a3d 50%, #0d5f2a 100%)',
      position: 'relative', overflow: 'hidden', fontFamily: 'Inter, sans-serif',
    }}
  >
    <div style={{
      position: 'absolute', inset: 0,
      background: 'repeating-linear-gradient(0deg, rgba(0,0,0,0.06) 0 80px, transparent 80px 160px)',
    }} />

    <svg viewBox="0 0 1920 1080" width="1920" height="1080" style={{ position: 'absolute', inset: 0 }}>
      <g stroke="#ffffff" strokeWidth="6" fill="none" opacity="0.85">
        <rect x="30" y="30" width="1860" height="1020" />
        <line x1="30" y1="30" x2="1890" y2="30" strokeWidth="4" />
        <path d="M 800 30 A 160 160 0 0 0 1120 30" />
        <rect x="510" y="720" width="900" height="330" />
        <rect x="720" y="900" width="480" height="150" />
        <rect x="880" y="1050" width="160" height="30" fill="rgba(255,255,255,0.5)" stroke="#fff" strokeWidth="4" />
        <circle cx="960" cy="870" r="6" fill="#fff" />
        <path d="M 820 720 A 160 160 0 0 1 1100 720" />
      </g>
    </svg>

    <div style={{
      position: 'absolute', top: 50, left: 0, right: 0, textAlign: 'center',
      color: '#ffe600', fontSize: 68, fontWeight: 900,
      letterSpacing: 6, textShadow: '4px 4px 0 rgba(0,0,0,0.55)',
      textTransform: 'uppercase', zIndex: 20, pointerEvents: 'none',
    }}>
      ESCALAÇÃO · <span style={{ color: '#fff' }}>{teamName}</span>
    </div>

    {(players || []).map((pl) => {
      const pos = SLOT_POSITIONS[pl.slot] || SLOT_POSITIONS.AE;
      const info = playerMap[pl.name] || {};
      return (
        <div key={pl.name} style={{
          position: 'absolute',
          top: pos.top, left: pos.left,
          transform: 'translate(-50%, -50%)',
          width: 260,
        }}>
          <MiniCard name={pl.name} slotLabel={SLOT_LABELS[pl.slot]} info={info} />
        </div>
      );
    })}
  </div>
));
PitchCanvas.displayName = 'PitchCanvas';

const MiniCard = ({ name, slotLabel, info }) => {
  const bg = 'linear-gradient(160deg, #b58729 0%, #d8a73a 30%, #f3d765 55%, #d8a73a 85%, #8e6010 100%)';
  return (
    <div style={{
      position: 'relative', width: 260, height: 360,
      borderRadius: 24, overflow: 'hidden',
      background: bg,
      boxShadow: '0 12px 30px rgba(0,0,0,0.5), inset 0 0 0 3px #5b3d05',
    }}>
      {info.card_image_url && (
        <img
          src={info.card_image_url}
          alt=""
          crossOrigin="anonymous"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        />
      )}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0) 25%, rgba(0,0,0,0) 55%, rgba(0,0,0,0.7) 100%)',
      }} />
      <div style={{
        position: 'absolute', top: 12, left: 12,
        background: 'rgba(0,0,0,0.65)', color: '#ffe600',
        padding: '4px 10px', borderRadius: 6, fontSize: 13, fontWeight: 900,
        letterSpacing: 2, textTransform: 'uppercase',
      }}>{slotLabel}</div>
      <div style={{
        position: 'absolute', bottom: 14, left: 0, right: 0, textAlign: 'center',
        color: '#fff', fontSize: 24, fontWeight: 900,
        letterSpacing: 1, textShadow: '2px 2px 0 rgba(0,0,0,0.6)',
        textTransform: 'uppercase',
        padding: '0 10px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>{name}</div>
    </div>
  );
};

// ==================== PLAYER STAT CARDS OVERLAY ====================
// For video editing: FIFA-style card of each player of the live match,
// but bottom shows JOGOS / GOLOS / ASSIST. (monthly stats) instead of PAC/SHO/PAS.
const flagEmoji = (iso) => {
  if (!iso || iso.length !== 2) return '';
  const A = 0x1F1E6;
  const codes = iso.toUpperCase().split('').map(c => A + (c.charCodeAt(0) - 65));
  try { return String.fromCodePoint(...codes); } catch { return ''; }
};

const getCardTier = (r) => {
  if (r >= 85) return { bg: 'linear-gradient(160deg, #0b3a4a 0%, #0e6473 35%, #1d8fa3 65%, #f6e600 100%)', border: '#0e6473' };
  if (r >= 70) return { bg: 'linear-gradient(160deg, #b58729 0%, #d8a73a 30%, #f3d765 55%, #d8a73a 85%, #8e6010 100%)', border: '#8e6010' };
  return { bg: 'linear-gradient(160deg, #828588 0%, #c8cdd1 30%, #eef0f2 55%, #b9bcc0 85%, #5e6164 100%)', border: '#5e6164' };
};

const PlayerCardsOverlay = ({ live, onClose }) => {
  const [cards, setCards] = useState({});   // { name: cardData }
  const [loading, setLoading] = useState(true);
  const nodeRefs = useRef({});

  useEffect(() => {
    let alive = true;
    const load = async () => {
      const names = [
        ...(live?.team_a || []).map(p => p.name),
        ...(live?.team_b || []).map(p => p.name),
      ];
      const results = await Promise.all(names.map(n =>
        axios.get(`${API}/players/${encodeURIComponent(n)}/card`)
          .then(r => [n, r.data]).catch(() => [n, null])
      ));
      if (!alive) return;
      const map = {};
      for (const [n, d] of results) map[n] = d;
      setCards(map);
      setLoading(false);
    };
    load();
    return () => { alive = false; };
  }, [live]);

  const downloadOne = async (name) => {
    const node = nodeRefs.current[name];
    if (!node) return;
    try {
      const url = await toPng(node, {
        pixelRatio: 2, cacheBust: true, width: 900, height: 1200,
      });
      const link = document.createElement('a');
      link.download = `carta_${name.replace(/\s+/g, '_')}.png`;
      link.href = url;
      link.click();
    } catch (e) { alert('Erro: ' + e.message); }
  };

  const downloadAll = async () => {
    const names = Object.keys(cards).filter(n => cards[n]);
    for (const n of names) {
      // Small delay between downloads to avoid browser suppression
      await downloadOne(n);
      await new Promise(r => setTimeout(r, 350));
    }
  };

  const allPlayers = [
    ...(live?.team_a || []).map(p => ({ ...p, team: 'a' })),
    ...(live?.team_b || []).map(p => ({ ...p, team: 'b' })),
  ];

  return (
    <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4" onClick={onClose}>
      <div className="max-w-6xl w-full max-h-[92vh] overflow-y-auto bg-zinc-900 rounded-2xl border-4 border-purple-500" onClick={(e) => e.stopPropagation()} data-testid="player-cards-modal">
        <div className="sticky top-0 bg-zinc-900 border-b border-zinc-700 p-4 flex items-center justify-between z-10">
          <div>
            <h3 className="text-2xl font-black uppercase text-white flex items-center gap-2">
              <IdCard size={22} /> Cartas dos Jogadores
            </h3>
            <p className="text-xs text-zinc-400">PNG 900×1200 (3:4) · usa a imagem da carta + rating, mas mostra <b>Jogos, Golos e Assistências do mês</b>. Ideal para edição de vídeo.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={downloadAll} className="bg-yellow-500 hover:bg-yellow-400 text-black font-black px-3 py-2 rounded flex items-center gap-2 text-sm" data-testid="download-all-cards">
              <Download size={16} /> BAIXAR TODAS
            </button>
            <button onClick={onClose} className="bg-white/10 hover:bg-white/20 text-white p-2 rounded" data-testid="close-cards">
              <X size={20} />
            </button>
          </div>
        </div>

        {loading ? (
          <p className="text-zinc-400 text-center py-12">A carregar cartas…</p>
        ) : (
          <div className="p-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {allPlayers.map((pl) => {
              const d = cards[pl.name];
              return (
                <div key={pl.name} className="flex flex-col items-center gap-2">
                  {/* Scaled preview (real 900x1200 card exists off-screen) */}
                  <div className={`w-full aspect-[3/4] rounded-lg border-2 ${pl.team === 'a' ? 'border-red-500' : 'border-blue-500'} overflow-hidden bg-zinc-800 relative`}>
                    {d ? (
                      <div style={{ width: 900, height: 1200, transform: 'scale(0.2)', transformOrigin: 'top left' }}>
                        <StatCardRenderer
                          ref={(el) => { nodeRefs.current[pl.name] = el; }}
                          data={d}
                        />
                      </div>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xs text-zinc-500">Sem dados</div>
                    )}
                  </div>
                  <button
                    onClick={() => downloadOne(pl.name)}
                    className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-black px-2 py-1.5 rounded text-xs flex items-center justify-center gap-1"
                    data-testid={`download-card-${pl.name}`}
                    disabled={!d}
                  >
                    <Download size={12} /> {pl.name.length > 14 ? pl.name.slice(0, 14) + '…' : pl.name}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// Renders a 900×1200 card DOM node (used for html-to-image capture)
const StatCardRenderer = React.forwardRef(({ data }, ref) => {
  const tier = getCardTier(data.rating);
  const hasBg = !!data.card_image_url;
  const flag = flagEmoji(data.nationality);
  const games = data.raw_stats?.games_played || 0;
  const goals = data.raw_stats?.goals || 0;
  const assists = data.raw_stats?.assists || 0;
  const shadow = '0 4px 14px rgba(0,0,0,.9), 0 0 4px rgba(0,0,0,1)';
  return (
    <div
      ref={ref}
      style={{
        width: 900, height: 1200, position: 'relative', overflow: 'hidden',
        borderRadius: 60, background: tier.bg,
        boxShadow: `inset 0 0 0 8px ${tier.border}`,
        fontFamily: 'Inter, sans-serif',
      }}
    >
      {hasBg && (
        <img
          src={data.card_image_url}
          alt=""
          crossOrigin="anonymous"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        />
      )}
      {/* Vignette */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(to bottom, rgba(0,0,0,.55) 0%, rgba(0,0,0,0) 22%, rgba(0,0,0,0) 50%, rgba(0,0,0,.65) 88%, rgba(0,0,0,.85) 100%)',
      }} />

      {/* Top-left: rating / position / flag */}
      <div style={{
        position: 'absolute', top: 48, left: 60,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, zIndex: 5,
      }}>
        <span style={{ fontSize: 150, fontWeight: 900, color: '#fff', textShadow: shadow, lineHeight: 1 }}>{data.rating}</span>
        <span style={{ fontSize: 48, fontWeight: 900, color: '#fff', letterSpacing: 6, textShadow: shadow, lineHeight: 1 }}>{data.position_short}</span>
        {flag && <span style={{ fontSize: 70, filter: 'drop-shadow(0 4px 8px rgba(0,0,0,.7))', lineHeight: 1 }}>{flag}</span>}
      </div>

      {/* Bottom overlay: name + monthly stats */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        padding: '30px 60px 60px 60px',
        background: 'linear-gradient(to top, rgba(0,0,0,.8) 0%, rgba(0,0,0,.4) 70%, rgba(0,0,0,0) 100%)',
        zIndex: 5,
      }}>
        <div style={{
          fontSize: 76, fontWeight: 900, color: '#fff', textAlign: 'center',
          textTransform: 'uppercase', letterSpacing: 2, textShadow: shadow,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          lineHeight: 1.1,
        }}>{data.name}</div>
        <div style={{ height: 4, background: 'rgba(255,255,255,.7)', margin: '18px 40px', borderRadius: 4 }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, textAlign: 'center' }}>
          <StatBlock label="JOGOS" value={games} shadow={shadow} />
          <StatBlock label="GOLOS" value={goals} shadow={shadow} />
          <StatBlock label="ASSIST." value={assists} shadow={shadow} />
        </div>
        <p style={{
          fontSize: 22, fontWeight: 700, color: 'rgba(255,255,255,.7)',
          textAlign: 'center', marginTop: 24, letterSpacing: 4, textTransform: 'uppercase',
          textShadow: shadow,
        }}>Estatísticas do mês</p>
      </div>
    </div>
  );
});
StatCardRenderer.displayName = 'StatCardRenderer';

const StatBlock = ({ label, value, shadow }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
    <span style={{ fontSize: 110, fontWeight: 900, color: '#fff', textShadow: shadow, lineHeight: 1 }}>{value}</span>
    <span style={{ fontSize: 26, fontWeight: 900, color: '#fff', letterSpacing: 4, textShadow: shadow, marginTop: 4 }}>{label}</span>
  </div>
);

export default LiveGameTab;
